/**
 * Platform Detection Utilities
 * Detect device type and browser capabilities
 */

/**
 * Check if user is on a mobile device
 */
export function isMobileDevice(): boolean {
  // Check user agent
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

  // Check for mobile patterns in user agent
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
  const isMobileUA = mobileRegex.test(userAgent.toLowerCase());

  // Check for touch support
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Check screen size (consider tablets as mobile for this purpose)
  const isSmallScreen = window.innerWidth <= 1024;

  return isMobileUA || (hasTouch && isSmallScreen);
}

/**
 * Check if user is on iOS
 */
export function isIOSDevice(): boolean {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  return /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
}

/**
 * iPhone / iPad / iPod Safari plus iPadOS "desktop" Safari (reports as MacIntel with touch).
 * Use for WebKit video / native HLS quirks (decoder limits, duplicate video elements).
 */
export function isIOSLike(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream) return true;
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;
  return false;
}

/**
 * Check if user is on Android
 */
export function isAndroidDevice(): boolean {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  return /android/i.test(userAgent);
}

/**
 * Check if browser supports screen recording (getDisplayMedia)
 */
export function supportsScreenRecording(): boolean {
  return !!(
    navigator.mediaDevices &&
    typeof navigator.mediaDevices.getDisplayMedia === 'function' &&
    typeof MediaRecorder !== 'undefined' &&
    !isMobileDevice() &&
    !isIOSLike() // iOS/iPadOS may expose desktop-class APIs but still can't reliably record
  );
}

/**
 * Check if user is on a desktop device
 */
export function isDesktopDevice(): boolean {
  return !isMobileDevice();
}

/**
 * Get platform name
 */
export function getPlatformName(): string {
  if (isIOSDevice()) return 'iOS';
  if (isAndroidDevice()) return 'Android';
  if (navigator.platform.includes('Mac')) return 'macOS';
  if (navigator.platform.includes('Win')) return 'Windows';
  if (navigator.platform.includes('Linux')) return 'Linux';
  return 'Unknown';
}

/**
 * Get device info for debugging
 */
export function getDeviceInfo() {
  return {
    platform: getPlatformName(),
    isMobile: isMobileDevice(),
    isDesktop: isDesktopDevice(),
    isIOS: isIOSDevice(),
    isAndroid: isAndroidDevice(),
    supportsScreenRecording: supportsScreenRecording(),
    userAgent: navigator.userAgent,
    screenSize: `${window.innerWidth}x${window.innerHeight}`,
    touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
  };
}
